import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { FaTrash, FaPlus, FaMinus, FaArrowRight, FaShoppingBasket, FaCreditCard, FaTruck, FaShoppingCart, FaLock } from 'react-icons/fa';
import { getCart, updateCartItemQuantity, removeFromCart, clearCart } from '../actions/cartActions';
import Loader from '../components/ui/Loader';
import Message from '../components/ui/Message';

const CartPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showConfirmClear, setShowConfirmClear] = useState(false);
  const [removingItemId, setRemovingItemId] = useState(null);
  const [updatingItemId, setUpdatingItemId] = useState(null);
  
  const { user } = useSelector(state => state.auth);
  const cart = useSelector(state => state.cart);
  const { loading, error, cartItems } = cart;
  
  useEffect(() => {
    if (user) {
      dispatch(getCart());
    }
  }, [dispatch, user]);
  
  const handleQuantityChange = (itemId, productId, currentQty, change) => {
    const newQty = currentQty + change;
    if (newQty > 0) {
      setUpdatingItemId(itemId);
      dispatch(updateCartItemQuantity(itemId, newQty))
        .finally(() => {
          setTimeout(() => setUpdatingItemId(null), 500);
        });
    }
  };
  
  const handleRemoveItem = (itemId) => {
    setRemovingItemId(itemId);
    dispatch(removeFromCart(itemId))
      .finally(() => {
        setTimeout(() => setRemovingItemId(null), 500);
      });
  };
  
  const handleClearCart = () => {
    setShowConfirmClear(false);
    dispatch(clearCart());
  };
  
  const checkoutHandler = () => {
    if (user) {
      navigate('/checkout');
    } else {
      navigate('/login?redirect=checkout');
    }
  };
  
  // Format price safely
  const formatPrice = (price) => {
    const numPrice = parseFloat(price);
    return isNaN(numPrice) ? 0 : numPrice;
  };
  
  // Calculate totals
  const calculateSubtotal = () => {
    return cartItems.reduce((acc, item) => acc + formatPrice(item.price) * item.quantity, 0);
  };
  
  const subtotal = calculateSubtotal();
  const shipping = subtotal > 200 ? 0 : 15; // Free shipping over ¥200
  const total = subtotal + shipping;
  
  // Get total items count
  const itemCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);
  
  // Mock featured products - in a real app, these would come from an API
  const featuredProducts = [
    {
      id: 1,
      name: "五粮液 52度 500ml",
      chinese_name: "五粮液 浓香型白酒",
      price: 1099,
      image_url: "/images/product1.jpg"
    },
    {
      id: 2,
      name: "剑南春 52度 500ml",
      chinese_name: "剑南春 浓香型白酒",
      price: 899,
      image_url: "/images/product2.jpg"
    },
    {
      id: 3,
      name: "茅台 53度 500ml",
      chinese_name: "茅台 酱香型白酒",
      price: 1299,
      image_url: "/images/product3.jpg"
    }
  ];
  
  return (
    <div className="cart-page">
      <div className="bg-gray-50 py-8 px-4 rounded-xl mb-8">
        <div className="container mx-auto">
          <h1 className="text-4xl font-bold mb-2">购物车</h1>
          <div className="text-gray-600">
            {itemCount > 0 ? `${itemCount} 件商品` : '您的购物车是空的'}
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4">
        {loading ? (
          <div className="py-12">
            <Loader />
          </div>
        ) : error ? (
          <Message variant="error">{error}</Message>
        ) : cartItems.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <div className="text-8xl text-gray-300 flex justify-center mb-6">
              <FaShoppingCart />
            </div>
            <h2 className="text-2xl font-bold mb-4">您的购物车是空的</h2>
            <p className="text-gray-600 mb-8">
              看起来您还没有添加任何商品到购物车。浏览我们的产品，寻找您喜欢的商品吧！
            </p>
            <Link 
              to="/products" 
              className="bg-red-700 text-white px-8 py-3 rounded-lg hover:bg-red-800 inline-flex items-center transition-colors"
            >
              <span>浏览商品</span>
              <FaArrowRight className="ml-2" />
            </Link>
            
            {/* Featured products if cart is empty */}
            <div className="mt-16">
              <h3 className="text-xl font-bold mb-6">热门推荐</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {featuredProducts.map(product => (
                  <div key={product.id} className="bg-white border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                    <Link to={`/product/${product.id}`}>
                      <div className="h-40 bg-gray-200">
                        <img 
                          src={product.image_url || '/images/default-wine.jpg'} 
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </Link>
                    <div className="p-4">
                      <Link to={`/product/${product.id}`} className="hover:text-red-700">
                        <h4 className="font-semibold">{product.name}</h4>
                      </Link>
                      <p className="text-gray-600 text-sm">{product.chinese_name}</p>
                      <div className="mt-2 flex justify-between items-center">
                        <span className="text-red-700 font-bold">¥{product.price.toFixed(2)}</span>
                        <button className="bg-red-700 text-white p-2 rounded-full hover:bg-red-800">
                          <FaPlus size={12} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-2/3">
              <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
                <div className="border-b py-4 px-6 bg-gray-50 flex items-center">
                  <FaShoppingBasket className="text-red-700 mr-2" />
                  <h2 className="text-xl font-semibold">购物清单</h2>
                </div>
                
                <div className="p-6">
                  <div className="hidden md:flex border-b pb-4 text-gray-600 font-medium">
                    <div className="w-2/5">商品</div>
                    <div className="w-1/5 text-center">单价</div>
                    <div className="w-1/5 text-center">数量</div>
                    <div className="w-1/5 text-right">小计</div>
                  </div>
                  
                  <div className="divide-y">
                    {cartItems.map(item => (
                      <div key={item.id} className="py-6 flex flex-col md:flex-row md:items-center">
                        <div className="w-full md:w-2/5 flex items-center mb-4 md:mb-0">
                          <div className="relative">
                            {removingItemId === item.id && (
                              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded">
                                <Loader size="sm" />
                              </div>
                            )}
                            <Link to={`/product/${item.product_id}`}>
                              <img 
                                src={item.image_url || '/images/default-wine.jpg'} 
                                alt={item.name}
                                className="w-20 h-20 object-cover rounded"
                              />
                            </Link>
                          </div>
                          <div className="ml-4">
                            <Link to={`/product/${item.product_id}`} className="font-medium hover:text-red-700 line-clamp-2">
                              {item.name}
                            </Link>
                            {item.chinese_name && (
                              <div className="text-sm text-gray-500">{item.chinese_name}</div>
                            )}
                            <button 
                              onClick={() => handleRemoveItem(item.id)}
                              className="text-red-600 hover:text-red-800 text-sm flex items-center mt-2 md:hidden"
                            >
                              <FaTrash size={12} className="mr-1" />
                              移除
                            </button>
                          </div>
                        </div>
                        
                        <div className="w-full md:w-1/5 text-left md:text-center mb-4 md:mb-0">
                          <div className="md:hidden text-gray-500 mb-1">单价</div>
                          <div className="font-medium">¥{formatPrice(item.price).toFixed(2)}</div>
                        </div>
                        
                        <div className="w-full md:w-1/5 text-left md:text-center mb-4 md:mb-0">
                          <div className="md:hidden text-gray-500 mb-1">数量</div>
                          <div className="flex items-center md:justify-center">
                            <button 
                              onClick={() => handleQuantityChange(item.id, item.product_id, item.quantity, -1)}
                              className="bg-gray-100 rounded-l p-2 hover:bg-gray-200 disabled:opacity-50"
                              disabled={updatingItemId === item.id || item.quantity <= 1}
                            >
                              <FaMinus size={12} />
                            </button>
                            <div className="relative">
                              {updatingItemId === item.id && (
                                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
                                  <div className="w-4 h-4 border-2 border-red-700 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                              )}
                              <span className="w-10 py-1 px-2 border-t border-b text-center">
                                {item.quantity}
                              </span>
                            </div>
                            <button 
                              onClick={() => handleQuantityChange(item.id, item.product_id, item.quantity, 1)}
                              className="bg-gray-100 rounded-r p-2 hover:bg-gray-200 disabled:opacity-50"
                              disabled={updatingItemId === item.id}
                            >
                              <FaPlus size={12} />
                            </button>
                          </div>
                        </div>
                        
                        <div className="w-full md:w-1/5 flex items-center justify-between md:justify-end">
                          <div>
                            <div className="md:hidden text-gray-500 mb-1">小计</div>
                            <div className="font-bold text-red-700">
                              ¥{(formatPrice(item.price) * item.quantity).toFixed(2)}
                            </div>
                          </div>
                          <button 
                            onClick={() => handleRemoveItem(item.id)}
                            className="text-gray-400 hover:text-red-600 hidden md:block"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="border-t py-4 px-6 bg-gray-50 flex flex-wrap justify-between items-center">
                  <button 
                    onClick={() => setShowConfirmClear(true)}
                    className="text-gray-700 flex items-center hover:text-red-700"
                  >
                    <FaTrash className="mr-2" />
                    清空购物车
                  </button>
                  
                  <Link 
                    to="/products" 
                    className="text-red-700 hover:text-red-800 flex items-center"
                  >
                    <FaShoppingCart className="mr-2" />
                    继续购物
                  </Link>
                </div>
              </div>
              
              {/* Shipping & Payment Info */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6 lg:mb-0">
                <div className="border-b py-4 px-6 bg-gray-50 flex items-center">
                  <FaTruck className="text-red-700 mr-2" />
                  <h2 className="text-xl font-semibold">配送与支付</h2>
                </div>
                
                <div className="p-6">
                  <div className="mb-6">
                    <h3 className="font-medium mb-3">配送方式</h3>
                    <div className="flex items-center p-3 border rounded-lg">
                      <input 
                        type="radio" 
                        id="standard-shipping" 
                        name="shipping" 
                        checked={true} 
                        readOnly
                        className="mr-3"
                      />
                      <label htmlFor="standard-shipping" className="flex-grow">
                        <div>标准配送</div>
                        <div className="text-sm text-gray-500">3-5个工作日送达</div>
                      </label>
                      <div className="font-medium">
                        {shipping > 0 ? `¥${shipping.toFixed(2)}` : '免费'}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      订单满¥200免运费，当前{subtotal >= 200 ? '已' : '未'}满足条件
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-medium mb-3">支付方式</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="border rounded-lg p-3 flex items-center opacity-75 cursor-not-allowed">
                        <input 
                          type="radio" 
                          id="pay-alipay" 
                          name="payment" 
                          disabled
                          className="mr-2"
                        />
                        <label htmlFor="pay-alipay">支付宝</label>
                      </div>
                      <div className="border rounded-lg p-3 flex items-center opacity-75 cursor-not-allowed">
                        <input 
                          type="radio" 
                          id="pay-wechat" 
                          name="payment"
                          disabled 
                          className="mr-2"
                        />
                        <label htmlFor="pay-wechat">微信支付</label>
                      </div>
                      <div className="border border-red-700 bg-red-50 rounded-lg p-3 flex items-center">
                        <input 
                          type="radio" 
                          id="pay-cod" 
                          name="payment" 
                          checked={true}
                          readOnly
                          className="mr-2"
                        />
                        <label htmlFor="pay-cod">货到付款</label>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      在线支付功能即将上线，敬请期待
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="lg:w-1/3">
              <div className="bg-white rounded-xl shadow-md overflow-hidden sticky top-4">
                <div className="border-b py-4 px-6 bg-gray-50 flex items-center">
                  <FaCreditCard className="text-red-700 mr-2" />
                  <h2 className="text-xl font-semibold">订单摘要</h2>
                </div>
                
                <div className="p-6">
                  <div className="space-y-3 border-b pb-4 mb-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600">商品小计</span>
                      <span>¥{subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">配送费</span>
                      <span>{shipping > 0 ? `¥${shipping.toFixed(2)}` : '免费'}</span>
                    </div>
                    {subtotal < 200 && (
                      <div className="text-xs text-gray-500 bg-yellow-50 p-2 rounded">
                        还差 ¥{(200 - subtotal).toFixed(2)} 即可享受免费配送
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-between items-center mb-6">
                    <span className="font-bold text-lg">订单总计</span>
                    <span className="font-bold text-2xl text-red-700">¥{total.toFixed(2)}</span>
                  </div>
                  
                  <button
                    onClick={checkoutHandler}
                    className="w-full bg-red-700 text-white py-3 rounded-lg hover:bg-red-800 flex items-center justify-center transition-colors"
                  >
                    <FaLock className="mr-2" />
                    去结算
                  </button>
                  
                  <div className="mt-4 text-sm text-gray-500 text-center">
                    点击"去结算"即表示您同意我们的
                    <Link to="/terms" className="text-red-700 hover:underline ml-1">服务条款</Link>
                  </div>
                  
                  <div className="flex items-center justify-center space-x-3 mt-6 text-gray-500">
                    <FaLock />
                    <span className="text-sm">安全支付</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Confirm Clear Cart Modal */}
      {showConfirmClear && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full animate-fadeIn">
            <h3 className="text-xl font-bold mb-4">清空购物车</h3>
            <p className="mb-6">确定要清空购物车吗？此操作无法撤销。</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirmClear(false)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={handleClearCart}
                className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800"
              >
                确认清空
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;